import React, { useCallback, useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { Loader, Stack } from '@lyearn/ui';
import * as ContentListModal from '@/components/ContentListModal';
import { ModalVariant } from '@/components/Modals/types';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useDebounceState from '@/hooks/useDebounceState';
import useContentMap from '@/hooks/useEntityMap';
import EmptyState from '@/modules/lyearn-connect/views/LyearnConnectManager/components/LyearnConnectList/EmptyState';
import { useSharedConnectionsQuery } from '@/modules/lyearn-connect/views/LyearnConnectManager/graphql/queries/generated/sharedConnections';
import { SharedConnectionInfo } from '@/types/schema';

import TertiaryAction from '../TertiaryAction';
import ConnectionSelector from './ConnectionSelector';
import { ShareDialogProps, WorkspaceMapping } from './types';

const ShareDialog: React.FC<ShareDialogProps> = (props) => {
  const { t } = useTranslation('LyearnConnect');
  const {
    onClose,
    onShare,
    alreadySharedWorkspaceIds,
    fetching = false,
    type = 'content',
    showTertiaryAction,
  } = props;
  const [searchStr, setSearchStr, debouncedSearchStr] = useDebounceState('');
  const [allowDuplication, { toggle }] = useToggle(false);
  const { [FeatureFlags.EnableSharedCourseDuplication]: enableSharedCourseDuplication } =
    useFeatureFlags();

  const [{ data: connectionData, fetching: connectionFetching, error: connectionError }] =
    useSharedConnectionsQuery({
      variables: {
        filters: {
          searchText: debouncedSearchStr,
        },
      },
    });
  const activeConnections = useMemo(
    () => connectionData?.sharedConnections.active?.edges?.map(({ node }) => node),
    [connectionData?.sharedConnections.active?.edges],
  ) as SharedConnectionInfo[];

  const {
    selectedItemsMap: selectedWorkspaceMap,
    toggle: toggleConnections,
    get: getConnections,
  } = useContentMap<WorkspaceMapping>();

  const _onShare = useCallback(() => {
    const selectedWorkspaces = Array.from(Object.values(selectedWorkspaceMap));
    if (!selectedWorkspaces.length) {
      onClose();
      return;
    }

    onShare(selectedWorkspaces, allowDuplication);
  }, [allowDuplication, onClose, onShare, selectedWorkspaceMap]);

  return (
    <ContentListModal.Main
      action={{
        discardText: t('Common:Cancel'),
        onSubmit: _onShare,
        text: t('Common:Share'),
        disabled: fetching,
        isSubmitting: fetching,
        TertiaryAction:
          showTertiaryAction && enableSharedCourseDuplication ? (
            <TertiaryAction allowDuplication={allowDuplication} onChange={toggle} />
          ) : null,
      }}
      header={{
        title: t('LyearnConnect:ShareDialog.Heading'),
        description: t('LyearnConnect:ShareDialog.Description', { type }),
      }}
      items={activeConnections}
      searchBar={{
        value: searchStr,
        onChange: setSearchStr,
        selectedCount: Object.values(selectedWorkspaceMap).length,
        placeholder: t('Common:Search'),
      }}
      variant={ModalVariant.ExtraWide}
      onClose={onClose}>
      {connectionFetching ? (
        <Stack alignItems="center" className="h-full w-full" justifyContent="center">
          <Loader />
        </Stack>
      ) : activeConnections?.length ? (
        <ContentListModal.Body
          className="flex w-full flex-1 flex-col border-b border-interactive-secondary-hover"
          itemRenderer={(index: number) => {
            const currentConnection = activeConnections[index];

            if (!currentConnection) {
              return null;
            }

            const { workspaceId, connectionId } = currentConnection;

            return (
              <ConnectionSelector
                key={workspaceId}
                multiSelect
                className={index === 0 ? '' : 'border-t'}
                connection={currentConnection}
                isAlreadySelected={alreadySharedWorkspaceIds?.includes(workspaceId)!}
                isSelected={!!getConnections(workspaceId)}
                onClick={() => toggleConnections(workspaceId, { workspaceId, connectionId })}
              />
            );
          }}
        />
      ) : (
        <EmptyState searchText={searchStr} />
      )}
    </ContentListModal.Main>
  );
};

export default ShareDialog;
